import { ArrowLeftOutlined } from '@ant-design/icons';
import { Alert, Button } from 'antd';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { useQuery } from 'urql';
import { monthLabel } from './format';
import { graphql } from './gql';
import { findUuid, looksLikeShortId, receiptNo } from './ids';

/** A member, meter, bill or payment found by its id or receipt number. */
export type FoundRecord = {
  kind: 'member' | 'meter' | 'bill' | 'payment';
  id: string;
  /** «الوصل 2026-00042 · أحمد …». */
  label: string;
  memberId: string | null;
  meterId: string | null;
  billId: string | null;
};

const NO_ID = '00000000-0000-0000-0000-000000000000';

const FindRecordDocument = graphql(`
  query FindRecord(
    $id: UUID!
    $byId: Boolean!
    $receiptNo: String!
    $byReceipt: Boolean!
  ) {
    member(id: $id) @include(if: $byId) {
      id
      fullName
    }
    meter(id: $id) @include(if: $byId) {
      id
      label
      memberId
      member {
        fullName
      }
    }
    bill(id: $id) @include(if: $byId) {
      id
      billMonth
      memberId
      meterId
      member {
        fullName
      }
    }
    payment(id: $id) @include(if: $byId) {
      ...FoundPayment
    }
    paymentByReceiptNo(receiptNo: $receiptNo) @include(if: $byReceipt) {
      ...FoundPayment
    }
  }

  fragment FoundPayment on Payment {
    id
    receiptNo
    billId
    bill {
      memberId
      meterId
      member {
        fullName
      }
    }
  }
`);

/**
 * What a search box's text points to, when it is an id (or a link with one)
 * or a receipt number: arqam is asked once, the apps' lists stay as they are.
 * `undefined` while asking; null when it's neither or nothing matched.
 */
export function useFindRecord(text: string): {
  found: FoundRecord | null | undefined;
  shortId: boolean;
} {
  const id = findUuid(text);
  const receipt = id ? null : receiptNo(text);
  const [{ data, fetching }] = useQuery({
    query: FindRecordDocument,
    variables: {
      id: id ?? NO_ID,
      byId: !!id,
      receiptNo: receipt ?? '',
      byReceipt: !!receipt,
    },
    pause: !id && !receipt,
  });
  const found = useMemo((): FoundRecord | null => {
    if (!data) return null;
    if (data.member) {
      return {
        kind: 'member',
        id: data.member.id,
        label: `العضو ${data.member.fullName}`,
        memberId: data.member.id,
        meterId: null,
        billId: null,
      };
    }
    if (data.meter) {
      return {
        kind: 'meter',
        id: data.meter.id,
        label: `العداد ${data.meter.label}${data.meter.member ? ` · ${data.meter.member.fullName}` : ''}`,
        memberId: data.meter.memberId,
        meterId: data.meter.id,
        billId: null,
      };
    }
    if (data.bill) {
      return {
        kind: 'bill',
        id: data.bill.id,
        label: `فاتورة ${monthLabel(data.bill.billMonth.slice(0, 7) as `${number}-${number}`)}${data.bill.member ? ` · ${data.bill.member.fullName}` : ''}`,
        memberId: data.bill.memberId,
        meterId: data.bill.meterId,
        billId: data.bill.id,
      };
    }
    const p = data.payment ?? data.paymentByReceiptNo;
    if (p) {
      return {
        kind: 'payment',
        id: p.id,
        // The number kept left to right inside the Arabic text.
        label: `الوصل \u2066${p.receiptNo}\u2069${p.bill?.member ? ` · ${p.bill.member.fullName}` : ''}`,
        memberId: p.bill?.memberId ?? null,
        meterId: p.bill?.meterId ?? null,
        billId: p.billId,
      };
    }
    return null;
  }, [data]);
  if (!id && !receipt) return { found: null, shortId: looksLikeShortId(text) };
  return { found: fetching && !data ? undefined : found, shortId: false };
}

/**
 * Above a list: what the pasted id or receipt number is, with «فتح». `href`
 * says where this app shows it (null: not in this app).
 */
export function FoundRecordCard({
  text,
  href,
}: {
  text: string;
  href: (found: FoundRecord) => string | null;
}) {
  const { found, shortId } = useFindRecord(text);
  if (shortId) {
    return (
      <Alert
        type="info"
        showIcon
        message="هذا جزء من المعرّف فقط: الصق المعرّف كاملاً (زر النسخ ينسخه كاملاً)."
      />
    );
  }
  if (found === undefined) return null;
  if (found === null) {
    return findUuid(text) || receiptNo(text) ? (
      <Alert type="warning" showIcon message="لا شيء بهذا المعرّف أو الرقم." />
    ) : null;
  }
  const to = href(found);
  return (
    <Alert
      type="success"
      showIcon
      message={found.label}
      action={
        to ? (
          <Link to={to}>
            <Button type="primary">
              فتح <ArrowLeftOutlined />
            </Button>
          </Link>
        ) : undefined
      }
      description={to ? undefined : 'لا يُعرض في هذا التطبيق.'}
    />
  );
}
